import { Badge, Card, CardContent, cn } from "@exhousing/design-system";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Home,
  Info,
  User,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { ContractTimeline } from "@/components/contract/ContractTimeline";
import { useContractBilling } from "@/hooks/api/useContractBilling";
import { useCostAdjustments } from "@/hooks/api/useCostAdjustments";
import { useDeposit } from "@/hooks/api/useDeposit";
import { useIndexations } from "@/hooks/api/useFinance";
import { useContractInvoices } from "@/hooks/api/useInvoices";
import { buildTimeline } from "@/lib/contractTimeline";
import { money } from "@/lib/money";
import type { components } from "@/types/api";

type Contract = components["schemas"]["ContractResponse"];
type ContractTab = "overview" | "terms" | "billing" | "deposit" | "documents" | "activity";

const OPEN_STATUSES = new Set(["issued", "overdue"]);

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - new Date(todayISO()).getTime();
  return Math.round(ms / 86_400_000);
}

/** Next monthly billing date for a given day-of-month (clamped to month length). */
function nextChargeDate(dayOfMonth: number): Date {
  const now = new Date();
  const clamp = (y: number, m: number) =>
    Math.min(dayOfMonth, new Date(y, m + 1, 0).getDate());
  let d = new Date(now.getFullYear(), now.getMonth(), clamp(now.getFullYear(), now.getMonth()));
  if (d < new Date(todayISO())) {
    d = new Date(now.getFullYear(), now.getMonth() + 1, clamp(now.getFullYear(), now.getMonth() + 1));
  }
  return d;
}

type Tone = "danger" | "warning" | "info" | "success";

function AlertRow({
  tone,
  children,
  actionLabel,
  onAction,
}: {
  tone: Tone;
  children: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const styles: Record<Tone, string> = {
    danger: "bg-danger-soft text-danger",
    warning: "bg-warning-soft text-warning",
    info: "bg-info-soft text-info",
    success: "bg-success-soft text-success",
  };
  const Icon = tone === "success" ? CheckCircle2 : tone === "info" ? Info : AlertTriangle;
  return (
    <div className={cn("flex items-center gap-3 rounded-lg px-4 py-3 text-sm", styles[tone])}>
      <Icon className="size-4 shrink-0" />
      <span className="flex-1">{children}</span>
      {onAction && actionLabel && (
        <button
          onClick={onAction}
          className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
        >
          {actionLabel}
          <ArrowRight className="size-3.5" />
        </button>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
  subTone = "muted",
}: {
  label: string;
  value: string;
  sub?: string;
  subTone?: "muted" | "danger" | "success";
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl tabular-nums">{value}</p>
      {sub && (
        <p
          className={cn(
            "mt-0.5 text-xs",
            subTone === "danger" && "text-danger",
            subTone === "success" && "text-success",
            subTone === "muted" && "text-muted-foreground",
          )}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

function statusSentence(contract: Contract): string {
  switch (contract.status) {
    case "draft":
      return "This contract is still a draft.";
    case "active":
      return `Active since ${fmtDate(contract.activated_at ?? contract.start_date)} · ${contract.term} term.`;
    case "ending":
      return `Winding down — ends ${fmtDate(contract.end_date)}.`;
    case "closed":
      return `Closed on ${fmtDate(contract.closed_at ?? contract.end_date)}.`;
    case "cancelled":
      return "This contract was cancelled.";
    default:
      return contract.status;
  }
}

export function ContractOverview({
  contract,
  onGoToTab,
}: {
  contract: Contract;
  onGoToTab: (tab: ContractTab) => void;
}) {
  const { data: invoices } = useContractInvoices(contract.id);
  const { data: deposit } = useDeposit(contract.id);
  const { data: billing } = useContractBilling(contract.id);
  const { data: indexations } = useIndexations(contract.id);
  const { data: adjustments } = useCostAdjustments(contract.id);

  const inv = invoices ?? [];
  const open = inv.filter((i) => OPEN_STATUSES.has(i.status));
  const overdue = open.filter(
    (i) => i.status === "overdue" || (i.due_date != null && i.due_date < todayISO()),
  );
  const outstanding = open.reduce((s, i) => s + i.total, 0);
  const overdueTotal = overdue.reduce((s, i) => s + i.total, 0);

  const schedule = billing?.schedule ?? null;
  const monthly = schedule
    ? schedule.lines.reduce((s, l) => s + l.amount + l.amount * l.vat_rate, 0)
    : 0;

  const isOpenLifecycle = contract.status === "active" || contract.status === "ending";

  // --- attention strip -----------------------------------------------------
  const alerts: ReactNode[] = [];
  if (contract.status === "draft") {
    alerts.push(
      <AlertRow key="draft" tone="info">
        Draft — activate it (top right) to lock the terms and start billing.
      </AlertRow>,
    );
  }
  if (overdue.length > 0) {
    alerts.push(
      <AlertRow key="overdue" tone="danger" actionLabel="View invoices" onAction={() => onGoToTab("billing")}>
        {money(overdueTotal)} overdue across {overdue.length} invoice
        {overdue.length > 1 ? "s" : ""}.
      </AlertRow>,
    );
  }
  if (contract.status === "active" && !schedule) {
    alerts.push(
      <AlertRow key="nobilling" tone="warning" actionLabel="Set up billing" onAction={() => onGoToTab("billing")}>
        Recurring billing isn't set up yet.
      </AlertRow>,
    );
  }
  if (isOpenLifecycle && deposit && !deposit.received_date) {
    alerts.push(
      <AlertRow key="deprec" tone="warning" actionLabel="Open deposit" onAction={() => onGoToTab("deposit")}>
        The deposit hasn't been marked as received.
      </AlertRow>,
    );
  }
  if (contract.status === "active" && contract.end_date) {
    const d = daysUntil(contract.end_date);
    if (d >= 0 && d <= 60) {
      alerts.push(
        <AlertRow key="ending" tone="warning">
          Ends in {d} day{d === 1 ? "" : "s"} ({fmtDate(contract.end_date)}).
        </AlertRow>,
      );
    }
  }
  if (
    (contract.status === "ending" || contract.status === "closed") &&
    deposit &&
    deposit.status !== "closed"
  ) {
    alerts.push(
      <AlertRow key="depsettle" tone="warning" actionLabel="Settle deposit" onAction={() => onGoToTab("deposit")}>
        The deposit still needs to be settled.
      </AlertRow>,
    );
  }
  if (contract.status === "active" && alerts.length === 0) {
    alerts.push(
      <AlertRow key="ok" tone="success">
        Everything's on track — nothing needs your attention.
      </AlertRow>,
    );
  }

  // --- metric tiles --------------------------------------------------------
  const depositTile = (() => {
    if (!deposit) {
      if (contract.deposit_amount && contract.deposit_amount > 0)
        return {
          value: money(contract.deposit_amount),
          sub: "agreed — not yet recorded" as string,
          tone: "muted" as const,
        };
      return { value: "—", sub: "not recorded" as string, tone: "muted" as const };
    }
    if (deposit.status === "closed")
      return { value: money(deposit.amount), sub: "settled", tone: "muted" as const };
    if (deposit.received_date)
      return { value: money(deposit.amount), sub: "held · secured", tone: "success" as const };
    return { value: money(deposit.amount), sub: "awaiting receipt", tone: "muted" as const };
  })();

  const nextChargeSub = (() => {
    if (!schedule) return "not set up";
    if (!schedule.is_active) return "paused";
    return `on ${fmtDate(nextChargeDate(schedule.day_of_month).toISOString())}`;
  })();
  const nextChargeValue = schedule && schedule.is_active ? money(monthly) : schedule ? "Paused" : "—";

  const events = buildTimeline(
    contract,
    inv,
    indexations ?? [],
    deposit ?? null,
    adjustments ?? [],
  );

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="font-display text-xl text-foreground">{statusSentence(contract)}</p>
        {alerts.length > 0 && <div className="space-y-2">{alerts}</div>}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric
          label="Outstanding"
          value={money(outstanding)}
          sub={
            overdue.length
              ? `${overdue.length} overdue`
              : open.length
                ? `${open.length} open`
                : "all settled"
          }
          subTone={overdue.length ? "danger" : open.length ? "muted" : "success"}
        />
        <Metric label="Deposit" value={depositTile.value} sub={depositTile.sub} subTone={depositTile.tone} />
        <Metric label="Next charge" value={nextChargeValue} sub={nextChargeSub} />
        <Metric
          label="Rent / mo"
          value={money(contract.rental_price)}
          sub={contract.rent_indexation_date ? `indexed ${fmtDate(contract.rent_indexation_date)}` : `${contract.term} term`}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardContent className="space-y-4 pt-6">
            <h3 className="text-sm font-medium">People &amp; place</h3>

            <div className="flex items-start gap-3">
              <User className="mt-0.5 size-4 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Tenant{contract.tenants.length > 1 ? "s" : ""}
                </p>
                {contract.tenants.length === 0 ? (
                  <p className="text-sm text-muted-foreground">None linked</p>
                ) : (
                  <ul className="mt-0.5 space-y-0.5">
                    {contract.tenants.map((t) => (
                      <li key={t.tenant_id} className="flex items-center gap-2 text-sm">
                        <Link to={`/parties/tenants/${t.tenant_id}`} className="font-medium hover:text-primary">
                          {t.display_name}
                        </Link>
                        {t.is_primary && <Badge tone="brand">Primary</Badge>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Home className="mt-0.5 size-4 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Property</p>
                <Link
                  to={`/properties/${contract.property_id}`}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  View property →
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 border-t border-border/60 pt-4 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Start</p>
                <p className="tabular-nums">{fmtDate(contract.start_date)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">End</p>
                <p className="tabular-nums">{fmtDate(contract.end_date)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Recent activity</h3>
              {events.length > 0 && (
                <button
                  onClick={() => onGoToTab("activity")}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  View all
                  <ArrowRight className="size-3.5" />
                </button>
              )}
            </div>
            <ContractTimeline events={events} limit={5} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
